import { AutomatedInvite } from "@/models/automated_invite";
import { type Module } from "vuex";
import type { State as RootState } from "@/stores";
import type { AutomatedInviteRequest, DefaultApi } from "@/api";

export interface State {
  automatedInvite?: AutomatedInvite;
}

// The store has one shared invite slot. A position epoch prevents responses
// from a previously viewed position from replacing it, while per-operation
// tokens let configuration saves and pause writes settle independently.
let latestRequestedPositionId: number | undefined;
let latestPositionEpoch = 0;
let latestRequestToken = 0;
let latestPeekToken: number | undefined;
let latestPostToken = 0;
let latestPauseToken = 0;

const beginPositionRequest = (positionId: number): number => {
  if (latestRequestedPositionId !== positionId) {
    latestRequestedPositionId = positionId;
    latestPositionEpoch++;
  }
  return latestPositionEpoch;
};

const isCurrentPositionRequest = (positionId: number, epoch: number): boolean =>
  latestRequestedPositionId === positionId && latestPositionEpoch === epoch;

const isConfigured = (automatedInvite: AutomatedInvite): boolean =>
  (automatedInvite.flags?.length ?? 0) > 0;

const buildAutoInviteState = (
  positionId: number,
  automatedInvite: AutomatedInvite,
): { positionId: number; configured: boolean } => ({
  positionId,
  configured: isConfigured(automatedInvite),
});

// Shared by postAutomatedInvite and applySavedAutomatedInvite. The keyed root state always
// reflects the save; the shared slot only does while slotIsCurrent.
const applySavedInviteState = (
  commit: (
    mutation: string,
    payload?: unknown,
    options?: { root: boolean },
  ) => void,
  positionId: number,
  automatedInvite: AutomatedInvite,
  slotIsCurrent: boolean,
): void => {
  if (slotIsCurrent) {
    commit("setAutomatedInviteConfiguration", automatedInvite);
  }
  commit(
    "position/setAutoInviteState",
    buildAutoInviteState(positionId, automatedInvite),
    { root: true },
  );
};

export const module: Module<State, RootState> = {
  namespaced: true,
  state: {
    automatedInvite: undefined,
  },
  actions: {
    async getAutomatedInviteSourceFilterOptions() {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      return (await api.getAutomatedInviteSourceFilterOptions()).data;
    },
    // `commit: false` (a reconciliation peek) joins the same request token/epoch but never
    // writes to the slot; a response superseded by a newer request throws, not resolves.
    async getAutomatedInvite(
      { commit },
      arg: number | { positionId: number; commit?: boolean },
    ) {
      const positionId = typeof arg === "number" ? arg : arg.positionId;
      const shouldCommit = typeof arg === "number" || arg.commit !== false;
      const epoch = beginPositionRequest(positionId);
      const token = ++latestRequestToken;
      const isStillCurrent = () =>
        token === latestRequestToken &&
        isCurrentPositionRequest(positionId, epoch);

      try {
        const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
          root: true,
        });
        const response = await api.getAutomatedInvite(positionId);
        const automatedInvite = new AutomatedInvite(response.data);
        if (!shouldCommit) {
          if (!isStillCurrent()) throw new Error("Stale automated invite peek");
          latestPeekToken = token;
          return automatedInvite;
        }
        if (isStillCurrent()) {
          commit("setAutomatedInvite", automatedInvite);
        }
        return automatedInvite;
      } catch (error) {
        if (shouldCommit && isStillCurrent()) {
          commit("setAutomatedInvite", undefined);
        }
        throw error;
      }
    },
    // Reflects a save that already landed (a live POST or a matching reconciliation peek)
    // through the same guard and derived update postAutomatedInvite's own success path uses.
    applySavedAutomatedInvite(
      { commit },
      {
        positionId,
        automatedInvite,
      }: { positionId: number; automatedInvite: AutomatedInvite },
    ) {
      const slotIsCurrent =
        latestRequestedPositionId === positionId &&
        latestRequestToken === latestPeekToken;
      applySavedInviteState(commit, positionId, automatedInvite, slotIsCurrent);
    },
    async postAutomatedInvite(
      { commit },
      {
        positionId,
        automatedInviteRequest,
      }: { positionId: number; automatedInviteRequest: AutomatedInviteRequest },
    ) {
      const epoch = beginPositionRequest(positionId);
      ++latestRequestToken;
      const token = ++latestPostToken;
      // async/await so a getAuthorizedApi rejection propagates instead of leaving
      // this promise pending forever.
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postAutomatedInvite(
        positionId,
        automatedInviteRequest,
      );
      const automatedInvite = new AutomatedInvite(response.data);
      const slotIsCurrent =
        token === latestPostToken &&
        isCurrentPositionRequest(positionId, epoch);
      applySavedInviteState(commit, positionId, automatedInvite, slotIsCurrent);
      return automatedInvite;
    },
    async patchAutomatedInvitePaused(
      { commit },
      { positionId, paused }: { positionId: number; paused: boolean },
    ) {
      const epoch = beginPositionRequest(positionId);
      ++latestRequestToken;
      const token = ++latestPauseToken;
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.patchAutomatedInvitePaused(positionId, {
        paused,
      });
      const automatedInvite = new AutomatedInvite(response.data);
      if (
        token === latestPauseToken &&
        isCurrentPositionRequest(positionId, epoch)
      ) {
        commit("setAutomatedInvitePaused", automatedInvite);
      }
      commit(
        "position/setAutoInviteState",
        { positionId, paused: automatedInvite.paused ?? false },
        { root: true },
      );
      return automatedInvite;
    },
  },
  mutations: {
    setAutomatedInvite(state, automatedInvite: AutomatedInvite) {
      state.automatedInvite = automatedInvite;
    },
    setAutomatedInviteConfiguration(state, automatedInvite: AutomatedInvite) {
      const current = state.automatedInvite;
      state.automatedInvite = new AutomatedInvite({
        ...automatedInvite,
        paused:
          current?.position_id === automatedInvite.position_id
            ? current.paused
            : automatedInvite.paused,
      });
    },
    setAutomatedInvitePaused(state, automatedInvite: AutomatedInvite) {
      const current = state.automatedInvite;
      state.automatedInvite = new AutomatedInvite({
        ...automatedInvite,
        ...(current?.position_id === automatedInvite.position_id
          ? current
          : {}),
        paused: automatedInvite.paused,
      });
    },
  },
};
